/**
 * @file Derives the progress header, the footer counts and the time left of one Signify fetch job
 * from the job document and its part documents, at read time. Consumers:
 * `SignifyFetchJobProgressHeader`, `SignifyFetchJobProgressActions`, `SignifyFetchJobMenuLabel`.
 * Pure: no store, no clock — `now` is an argument. The job document stores no counts, so this is
 * the only place the numbers exist; two consumers can never disagree.
 * The ETA is a sliding window over `completedAt`, so a cache-hit burst inflates it briefly and a
 * slowdown deflates it within a minute; it is null until the window holds enough samples.
 * "Slowed" is the live cap against the capacity the dispatch started at, not the congestion pause:
 * the pause lasts seconds while the halved cap lasts the rest of the dispatch, and that is the
 * stretch the operator is waiting through. It is reported only while a dispatch is still owed, so a
 * finished job does not go on saying it is being slowed by a cap nothing is waiting on. `initial` is
 * read defensively because jobs written before it existed carry no such field — those simply never
 * report the line.
 * Both inputs are the narrowest shapes the derivation reads, which is also what makes a valtio
 * snapshot assignable to them — `Snapshot<SignifyFetchJob>` is not a `SignifyFetchJob`.
 */

import type { SignifyFetchJobPhase, SignifyFetchPartState } from '#types';

/** A `running` job whose heartbeat is older than this is shown as stalled. */
export const SIGNIFY_FETCH_STALL_MS = 120_000;
const ETA_WINDOW_MS = 60_000;
const ETA_MIN_SAMPLES = 3;

export type SignifyFetchJobProgressSource = {
  phase: SignifyFetchJobPhase;
  heartbeatAt: number;
  concurrency: { cap: number; initial?: number };
};

export type SignifyFetchPartProgressSource =
  | { state: Exclude<SignifyFetchPartState, 'done' | 'failed'>; insertedAt: number | null }
  | { state: 'done' | 'failed'; insertedAt: number | null; completedAt: number };

export type SignifyFetchJobProgress = {
  total: number;
  done: number;
  failed: number;
  ambiguous: number;
  /** Parts still `pending` or `running`; what the ETA counts down. */
  unfetched: number;
  /** `done` parts not yet inserted — the Insert button's number. */
  ready: number;
  status: 'queued' | 'running' | 'stalled' | 'completed' | 'cancelled';
  /** The gate capacity while it is below the capacity the dispatch started at, else null. */
  slowedCap: number | null;
  /** Milliseconds left, or null when not running, nothing left, or too few samples. */
  etaMs: number | null;
};

type Counts = Pick<SignifyFetchJobProgress, 'done' | 'failed' | 'ambiguous' | 'ready'> & {
  recent: number;
};

const EMPTY: Counts = { done: 0, failed: 0, ambiguous: 0, ready: 0, recent: 0 };

const count = (parts: readonly SignifyFetchPartProgressSource[], now: number): Counts =>
  parts.reduce<Counts>(
    (acc, part) => ({
      done: acc.done + (part.state === 'done' ? 1 : 0),
      failed: acc.failed + (part.state === 'failed' ? 1 : 0),
      ambiguous: acc.ambiguous + (part.state === 'ambiguous' ? 1 : 0),
      ready: acc.ready + (part.state === 'done' && part.insertedAt === null ? 1 : 0),
      recent:
        acc.recent +
        ((part.state === 'done' || part.state === 'failed') &&
        now - part.completedAt <= ETA_WINDOW_MS
          ? 1
          : 0),
    }),
    EMPTY,
  );

export const deriveSignifyFetchJobProgress = (
  job: SignifyFetchJobProgressSource | null,
  parts: readonly SignifyFetchPartProgressSource[],
  now: number,
): SignifyFetchJobProgress => {
  const { recent, ...counts } = count(parts, now);
  const unfetched = parts.length - counts.done - counts.failed - counts.ambiguous;
  const status =
    job === null
      ? 'queued'
      : job.phase === 'running' && now - job.heartbeatAt > SIGNIFY_FETCH_STALL_MS
        ? 'stalled'
        : job.phase;
  const dispatching = status !== 'completed' && status !== 'cancelled';
  const slowedCap =
    dispatching && job !== null && job.concurrency.cap < (job.concurrency.initial ?? 0)
      ? job.concurrency.cap
      : null;
  const etaMs =
    status === 'running' && unfetched > 0 && recent >= ETA_MIN_SAMPLES
      ? Math.round((unfetched * ETA_WINDOW_MS) / recent)
      : null;
  return { total: parts.length, ...counts, unfetched, status, slowedCap, etaMs };
};
